'use client';

/**
 * Footer of the capabilities browser: the two things the grid cannot say.
 *
 * Everything above is "what Meridian can do when you ask". These two links are
 * "…and it can do it unattended" and "…and you can give it more tools" — both
 * invisible from a list of capabilities, which is exactly why they live at the
 * bottom of the list of capabilities.
 *
 * Split out of the container to keep it under the module-size cap and because
 * neither link has any state.
 */

import React from 'react';
import { CalendarClock, Plug } from 'lucide-react';

export interface CapabilitiesFooterLinksProps {
  /** Routines page. Omit to hide the link. */
  routinesHref?: string;
  /** External MCP connections page (admin-only in Hub). Omit to hide the link. */
  mcpConnectionsHref?: string;
}

export function CapabilitiesFooterLinks({
  routinesHref,
  mcpConnectionsHref,
}: CapabilitiesFooterLinksProps) {
  if (!routinesHref && !mcpConnectionsHref) return null;

  return (
    <div className="shrink-0 space-y-3 border-t border-border pt-3">
      {routinesHref && (
        <div>
          <a
            href={routinesHref}
            className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
          >
            <CalendarClock className="h-4 w-4" />
            Run this on a schedule
          </a>
          <p className="mt-1 text-xs text-muted-foreground">
            A routine does any of the above unattended — as you, on a schedule or
            when an email or Slack message arrives — and delivers the result where
            you ask.
          </p>
        </div>
      )}

      {mcpConnectionsHref && (
        <div>
          <a
            href={mcpConnectionsHref}
            className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
          >
            <Plug className="h-4 w-4" />
            Connect external MCP servers
          </a>
          <p className="mt-1 text-xs text-muted-foreground">
            Add Model Context Protocol servers to give Meridian more tools. Admin only.
          </p>
        </div>
      )}
    </div>
  );
}
